import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getOpenPaymentItems, PAYMENT_MESSAGES } from '../../api/endpoints/payments';
import { RecordReceiptDialog } from './RecordReceiptDialog';
import { parsePaise } from './allocation';
import { useRecordReceipt } from './hooks';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';

function receiptContextError(error: unknown) {
  const candidate = error as {
    code?: keyof typeof PAYMENT_MESSAGES;
    response?: { data?: { code?: keyof typeof PAYMENT_MESSAGES } };
  } | null;
  const code = candidate?.code ?? candidate?.response?.data?.code;
  return code && Object.prototype.hasOwnProperty.call(PAYMENT_MESSAGES, code)
    ? PAYMENT_MESSAGES[code]
    : 'Could not load receipt context. Try again.';
}

/** Exact order lookup only: opening a contextual receipt never reads the shipper ledger. */
export function OrderReceiptDialog({
  shipmentId,
  shipperId,
  shipperName,
  onClose,
}: {
  shipmentId: number;
  shipperId: number;
  shipperName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['payments', 'open-items', 'IN', shipperId, 'order', shipmentId],
    queryFn: () => getOpenPaymentItems('IN', shipperId, { page: 0, size: 50, q: `#${shipmentId}` }),
    retry: false,
  });
  const receipt = useRecordReceipt();
  const items = useMemo(
    () =>
      query.data
        ? {
            ...query.data,
            orders:
              query.data.direction === 'IN' && query.data.counterpartyId === shipperId
                ? query.data.orders.filter(
                    (order) =>
                      order.shipmentId === shipmentId &&
                      ['LOADED', 'IN_TRANSIT', 'AT_DROP', 'DELIVERED', 'COMPLETED'].includes(
                        order.status,
                      ) &&
                      parsePaise(order.outstandingAmount) > 0n,
                  )
                : [],
          }
        : undefined,
    [query.data, shipmentId, shipperId],
  );
  if (items?.orders.length)
    return (
      <RecordReceiptDialog
        openItems={items}
        initialShipmentId={shipmentId}
        shipperName={shipperName}
        submitting={receipt.isPending}
        onClose={onClose}
        onSubmit={async (request) => {
          await receipt.mutateAsync({ request });
          onClose();
        }}
      />
    );
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.orderReceiptDialog.title')}
        className={`${styles.dialog} ${styles.compact}`}
      >
        <div className={styles.body}>
          {query.isPending ? (
            <p role="status">{t('payments.orderReceiptDialog.loading')}</p>
          ) : (
            <p role="alert">
              {query.isError
                ? receiptContextError(query.error)
                : `Order #${shipmentId} has no eligible outstanding receipt item. Close and refresh Payments.`}
              {query.isError && (
                <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
                  {t('payments.orderReceiptDialog.retry')}
                </Button>
              )}
            </p>
          )}
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
        </div>
      </section>
    </div>
  );
}
